import {
  faBusinessTime,
  faCalendarTimes,
  faCircleUser,
  faCode,
  faComputer,
  faKey,
  faCopy,
  faCheck,
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { HttpTransportType, HubConnectionBuilder } from "@microsoft/signalr";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FitAddon } from "@xterm/addon-fit";
import axios from "axios";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import SearchBox from "components/common/SearchBox";
import { servidoresBreadcrumbItems } from "data/servidores";
import { useEffect, useRef, useState } from "react";
import { Alert, Modal, Form, Table } from "react-bootstrap";
import Badge from "react-bootstrap/Badge";
import Card from "react-bootstrap/Card";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { Link } from "react-router-dom";
import { Terminal } from "xterm";
import "xterm/css/xterm.css";
import {
  VersaoDto,
} from "helpers/timelineAdapter";
import { format } from "date-fns";
import { Home, Info, Monitor, Save, Server, Settings, UploadCloud } from "lucide-react";

export interface EmpresaType {
  id: string;
}

interface BackendType {
  cnpj: string;
  razaoSocial: string;
  versao: string;
  standalone: boolean;
  configuracao: {
    producao: boolean;
    vendaNaoFiscal: boolean;
  };
  licenca: {
    id: string;
    hw: string;
    thumbprint: string;
    serial: string;
    issued: string;
    expiration: string;
    customer: string;
    roles: string[];
    pub: string;
  };
  registradoEm: string;
  statusSincronizacao?: {
    timestamp: string;
    since: string;
    status: number;
    mensagem: string;
  };
  empresa?: EmpresaType;
}

interface FrontendType {
  cnpj: string;
  razaoSocial: string;
  versao: string;
  maquinaId: string;
  usuario: string;
  registradoEm: string;
}

export interface ClienteType {
  connectionId: string;
  backend: BackendType;
  frontends: FrontendType[];
  cnpj: string;
  razaoSocial: string;
  versaoBackend: string;
  versaoGofaz: string;
  versaoAgente: string;
  hardwareId: string;
  standalone: boolean;
  homologacao: boolean;
  registradoEm: string;
  quantidadeTerminais: number;
  usuario: string;
  maquinaId: string;
  whitelabelHabilitado: boolean;
}

// Helper function to format CNPJ
const formatCNPJ = (cnpj: string): string => {
  if (!cnpj || cnpj.length !== 14) return cnpj;

  // Format as XX.XXX.XXX/XXXX-XX
  return `${cnpj.substring(0, 2)}.${cnpj.substring(2, 5)}.${cnpj.substring(
    5,
    8
  )}/${cnpj.substring(8, 12)}-${cnpj.substring(12)}`;
};

const fetchVersoes = async ({ queryKey }: any) => {
  const [, _] = queryKey;
  const { data } = await axios.get(`/api/hubs/cliente/atualizacoes`);
  return data;
};

const fetchClientes = async ({ queryKey }: any) => {
  const [, _] = queryKey;
  const { data } = await axios.get(`/api/hubs/cliente`);
  return data;
};

const fetchHealthcheck = async ({ queryKey }: any) => {
  const [, chave] = queryKey;
  const { data } = await axios.post(`/api/hubs/cliente/healthcheck/${chave}`);
  return data;
};

const HealthCheck = ({ connectionId }: { connectionId: string }) => {
  const {
    data: healthCheck,
    error,
    isLoading,
  } = useQuery({
    queryFn: fetchHealthcheck,
    queryKey: ["healthCheck", connectionId],
  });

  if (isLoading) {
    return <p>Carregando...</p>;
  }

  if (error) {
    console.log("Erro ao carregar healthcheck: ", error);
    return <p>Erro ao carregar healthcheck.</p>;
  }

  return <pre>{JSON.stringify(healthCheck, null, 2)}</pre>;
};

const Term = ({ connectionId }: { connectionId: string }) => {
  const terminalRef = useRef<HTMLDivElement>(null);
  const terminal = useRef<Terminal | null>(null);
  const connection = useRef<any>(null);

  useEffect(() => {
    terminal.current = new Terminal();
    const fitAddon = new FitAddon();
    terminal.current.loadAddon(fitAddon);
    terminal.current.open(terminalRef.current!);

    terminal.current.focus();
    fitAddon.fit();

    // Create and start the SignalR connection
    connection.current = new HubConnectionBuilder()
      .withUrl("/ws/BackendHub", {
        transport: HttpTransportType.WebSockets,
      })
      .build();

    connection.current.on("RetornoDeComandoRecebido", (output: string) => {
      terminal.current?.write(output + "\r\n");
    });

    connection.current
      .start()
      .then(() => {
        let buff = "";
        terminal.current?.onData((data: string) => {
          if (data.charCodeAt(0) === 13) {
            // Enter key
            connection.current
              .invoke("ExecutarComando", connectionId, buff)
              .catch((err: Error) => console.error(err.toString()));
            buff = "";
            terminal.current?.write("\r\n");
            return;
          }

          if (data.charCodeAt(0) === 127) {
            // Backspace key
            terminal.current?.write("\b \b");
            buff = buff.slice(0, -1);
            return;
          }

          //control + c
          if (data.charCodeAt(0) === 3) {
            connection.current
              .invoke("ExecutarComando", connectionId, "^C")
              .catch((err: Error) => console.error(err.toString()));
            buff = "";
            terminal.current?.write("^C");
            return;
          }

          terminal.current?.write(data);
          buff += data;
        });
      })
      .catch((err: Error) => console.error(err.toString()));

    return () => {
      connection.current?.stop();
    };
  }, [connectionId]);

  return <div ref={terminalRef} style={{ height: "100%", width: "100%" }} />;
};

const Atualizacao = ({ connectionId }: { connectionId: string }) => {
  const [selectedBackendVersion, setSelectedBackendVersion] = useState("");
  const [selectedFrontendVersion, setSelectedFrontendVersion] = useState("");
  const [aviso, setAviso] = useState("");
  const [avisoTipo, setAvisoTipo] = useState<"success" | "danger">("success");

  const { data, error, isLoading } = useQuery({
    queryFn: fetchVersoes,
    queryKey: ["atualizacoes"],
  });

  const mutation = useMutation({
    mutationFn: (data) => {
      return axios.post(`/api/hubs/Cliente/atualizar/${connectionId}`, data);
    },
    onError: (error: any) => {
      console.log("Erro ao atualizar versões: ", error);
      setAviso("Erro ao atualizar versões.");
      setAvisoTipo("danger");
    },
    onSuccess: () => {
      console.log("Versões atualizadas com sucesso.");
      setAviso("Versões atualizadas com sucesso.");
      setAvisoTipo("success");
    },
  });

  useEffect(() => {
    if (data) {
      // Filter versions for frontend (.msi.zip) and backend (backend-*.zip)
      const versoesFrontend = data.filter((versao: any) =>
        versao.link.match(/.*\.msi.zip$/)
      );
      const versoesBackend = data.filter((versao: any) =>
        versao.link.match(/backend-.*\.zip$/)
      );

      // Find the installer-marked version (not including "latest" in name)
      const installerFrontend = versoesFrontend.find(
        (versao: any) => !versao.nome.includes("latest")
      );
      const installerBackend = versoesBackend.find(
        (versao: any) => !versao.nome.includes("latest")
      );

      // Set the selected versions to installer-marked versions
      if (installerFrontend) setSelectedFrontendVersion(installerFrontend.link);
      if (installerBackend) setSelectedBackendVersion(installerBackend.link);
    }
  }, [data]);

  const handleSubmit = (event: any) => {
    event.preventDefault();
    mutation.mutate({
      backend: selectedBackendVersion,
      frontend: selectedFrontendVersion,
    } as any);
  };

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <span
          className="spinner-border spinner-border-sm"
          aria-hidden="true"
        ></span>
        <span className="ms-2">Carregando versões disponíveis...</span>
      </div>
    );
  }

  return (
    <form className="w-100" onSubmit={handleSubmit}>
      {aviso && (
        <Alert variant={avisoTipo} className="mb-3">
          {aviso}
        </Alert>
      )}

      <div className="mb-3">
        <h5>Versão do Backend</h5>
        <div className="table-responsive" style={{ maxHeight: "200px" }}>
          <Table hover>
            <thead className="sticky-top bg-body">
              <tr>
                <th></th>
                <th>Nome</th>
                <th>Tamanho</th>
                <th>Data</th>
              </tr>
            </thead>
            <tbody>
              {data
                ?.filter((versao: any) => versao.link.match(/backend-.*\.zip$/))
                .map((versao: any, index: number) => (
                  <tr
                    key={index}
                    className={
                      selectedBackendVersion === versao.link
                        ? "table-primary"
                        : ""
                    }
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelectedBackendVersion(versao.link)}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <Form.Check
                        type="radio"
                        name="backendVersion"
                        checked={selectedBackendVersion === versao.link}
                        onChange={() => setSelectedBackendVersion(versao.link)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td>{versao.nome}</td>
                    <td>{formatFileSize(versao.tamanho)}</td>
                    <td>
                      {format(new Date(versao.ultimaModificacao), 'dd/MM/yyyy HH:mm')}
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
      </div>

      <div className="mb-3">
        <h5>Versão do Frontend</h5>
        <div className="table-responsive" style={{ maxHeight: "200px" }}>
          <Table hover>
            <thead className="sticky-top bg-body">
              <tr>
                <th></th>
                <th>Nome</th>
                <th>Tamanho</th>
                <th>Data</th>
              </tr>
            </thead>
            <tbody>
              {data
                ?.filter((versao: any) => versao.link.match(/.*\.msi.zip$/))
                .map((versao: any, index: number) => (
                  <tr
                    key={index}
                    className={
                      selectedFrontendVersion === versao.link
                        ? "table-primary"
                        : ""
                    }
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelectedFrontendVersion(versao.link)}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <Form.Check
                        type="radio"
                        name="frontendVersion"
                        checked={selectedFrontendVersion === versao.link}
                        onChange={() => setSelectedFrontendVersion(versao.link)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td>{versao.nome}</td>
                    <td>{formatFileSize(versao.tamanho)}</td>
                    <td>
                      {format(new Date(versao.ultimaModificacao), 'dd/MM/yyyy HH:mm')}
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
      </div>

      <div className="d-flex justify-content-end mt-3">
        <Button
          variant="primary"
          type="submit"
          disabled={
            mutation.isPending ||
            !selectedBackendVersion ||
            !selectedFrontendVersion
          }
        >
          {mutation.isPending ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                aria-hidden="true"
              ></span>
              Atualizando...
            </>
          ) : (
            "Atualizar"
          )}
        </Button>
      </div>
    </form>
  );
};

// Version Selection Modal Component
const VersionSelectionModal = ({
  show,
  onHide,
  connectionId,
}: {
  show: boolean;
  onHide: () => void;
  connectionId: string;
}) => {
  const [selectedBackendVersion, setSelectedBackendVersion] = useState("");
  const [selectedFrontendVersion, setSelectedFrontendVersion] = useState("");
  const [aviso, setAviso] = useState("");
  const [avisoTipo, setAvisoTipo] = useState<"success" | "danger">("success");

  const { data, error, isLoading } = useQuery({
    queryFn: fetchVersoes,
    queryKey: ["atualizacoes"],
  });

  const mutation = useMutation({
    mutationFn: (data) => {
      return axios.post(`/api/hubs/Cliente/atualizar/${connectionId}`, data);
    },
    onError: (error: any) => {
      console.log("Erro ao atualizar versões: ", error);
      setAviso("Erro ao atualizar versões.");
      setAvisoTipo("danger");
    },
    onSuccess: () => {
      console.log("Versões atualizadas com sucesso.");
      setAviso("Versões atualizadas com sucesso.");
      setAvisoTipo("success");
      setTimeout(() => {
        onHide();
      }, 2000);
    },
  });

  useEffect(() => {
    if (data) {
      // Filter versions for frontend (.msi.zip) and backend (backend-*.zip)
      const versoesFrontend = data.filter((versao: any) =>
        versao.link.match(/.*\.msi.zip$/)
      );
      const versoesBackend = data.filter((versao: any) =>
        versao.link.match(/backend-.*\.zip$/)
      );

      // Find the installer-marked version (not including "latest" in name)
      const installerFrontend = versoesFrontend.find(
        (versao: any) => !versao.nome.includes("latest")
      );
      const installerBackend = versoesBackend.find(
        (versao: any) => !versao.nome.includes("latest")
      );

      // Set the selected versions to installer-marked versions
      if (installerFrontend) setSelectedFrontendVersion(installerFrontend.link);
      if (installerBackend) setSelectedBackendVersion(installerBackend.link);
    }
  }, [data]);

  const handleSubmit = (event: any) => {
    event.preventDefault();
    mutation.mutate({
      backend: selectedBackendVersion,
      frontend: selectedFrontendVersion,
    } as any);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Selecionar Versões para Atualização</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-4">
            <span
              className="spinner-border spinner-border-sm"
              aria-hidden="true"
            ></span>
            <span className="ms-2">Carregando versões disponíveis...</span>
          </div>
        ) : (
          <form className="w-100" onSubmit={handleSubmit}>
            {aviso && (
              <Alert variant={avisoTipo} className="mb-3">
                {aviso}
              </Alert>
            )}

            <div className="mb-3">
              <h5>Versão do Backend</h5>
              <div className="table-responsive" style={{ maxHeight: "200px" }}>
                <Table hover>
                  <thead className="sticky-top bg-body">
                    <tr>
                      <th></th>
                      <th>Nome</th>
                      <th>Tamanho</th>
                      <th>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data
                      ?.filter((versao: any) =>
                        versao.link.match(/backend-.*\.zip$/)
                      )
                      .map((versao: any, index: number) => (
                        <tr
                          key={index}
                          className={
                            selectedBackendVersion === versao.link
                              ? "table-primary"
                              : ""
                          }
                          style={{ cursor: "pointer" }}
                          onClick={() => setSelectedBackendVersion(versao.link)}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <Form.Check
                              type="radio"
                              name="backendVersion"
                              checked={selectedBackendVersion === versao.link}
                              onChange={() =>
                                setSelectedBackendVersion(versao.link)
                              }
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td>{versao.nome}</td>
                          <td>
                            {formatFileSize
                              ? formatFileSize(versao.tamanho)
                              : versao.tamanho}
                          </td>
                          <td>
                            {format(new Date(versao.ultimaModificacao), 'dd/MM/yyyy HH:mm')}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>
            </div>

            <div className="mb-3">
              <h5>Versão do Frontend</h5>
              <div className="table-responsive" style={{ maxHeight: "200px" }}>
                <Table hover>
                  <thead className="sticky-top bg-body">
                    <tr>
                      <th></th>
                      <th>Nome</th>
                      <th>Tamanho</th>
                      <th>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data
                      ?.filter((versao: any) =>
                        versao.link.match(/.*\.msi.zip$/)
                      )
                      .map((versao: any, index: number) => (
                        <tr
                          key={index}
                          className={
                            selectedFrontendVersion === versao.link
                              ? "table-primary"
                              : ""
                          }
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            setSelectedFrontendVersion(versao.link)
                          }
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <Form.Check
                              type="radio"
                              name="frontendVersion"
                              checked={selectedFrontendVersion === versao.link}
                              onChange={() =>
                                setSelectedFrontendVersion(versao.link)
                              }
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td>{versao.nome}</td>
                          <td>
                            {formatFileSize
                              ? formatFileSize(versao.tamanho)
                              : versao.tamanho}
                          </td>
                          <td>
                            {format(new Date(versao.ultimaModificacao), 'dd/MM/yyyy HH:mm')}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>
            </div>

            <div className="d-flex justify-content-end mt-3">
              <Button
                variant="secondary"
                onClick={onHide}
                className="me-2"
                disabled={mutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={
                  mutation.isPending ||
                  !selectedBackendVersion ||
                  !selectedFrontendVersion
                }
              >
                {mutation.isPending ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      aria-hidden="true"
                    ></span>
                    Atualizando...
                  </>
                ) : (
                  "Confirmar Atualização"
                )}
              </Button>
            </div>
          </form>
        )}
      </Modal.Body>
    </Modal>
  );
};

// Version Selection By ID Modal Component
const VersionSelectionByIdModal = ({
  show,
  onHide,
  connectionId,
}: {
  show: boolean;
  onHide: () => void;
  connectionId: string;
}) => {
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(
    null
  );
  const [aviso, setAviso] = useState("");
  const [avisoTipo, setAvisoTipo] = useState<"success" | "danger">("success");

  // Fetch versions from the admin/versao endpoint
  const { data, isLoading, error } = useQuery({
    queryKey: ["versoes-admin"],
    queryFn: async () => {
      const response = await axios.get("/api/admin/versao", {
        params: {
          pageSize: 100, // Get a large number to avoid pagination issues
          pageIndex: 0,
          sort: "createdAt,desc", // Sort by creation date (newest first)
        },
      });
      return response.data;
    },
    enabled: show, // Only fetch when modal is shown
  });

  // Update selection when data changes
  useEffect(() => {
    if (data?.data && Array.isArray(data.data)) {
      // Find the installer version
      const installerVersion = data.data.find((v: VersaoDto) => v.instalador);
      if (installerVersion) {
        setSelectedVersionId(installerVersion.id);
      } else if (data.data.length > 0) {
        // If no installer version, select the most recent one
        setSelectedVersionId(data.data[0].id);
      }
    }
  }, [data]);

  // Mutation to update the client with selected version ID
  const mutation = useMutation({
    mutationFn: (versionId: number) => {
      return axios.post(`/api/hubs/Cliente/atualizarPorId/${connectionId}`, {
        versionId,
      });
    },
    onError: (error: any) => {
      console.log("Erro ao atualizar versão:", error);
      setAviso("Erro ao atualizar versão.");
      setAvisoTipo("danger");
    },
    onSuccess: () => {
      console.log("Versão atualizada com sucesso.");
      setAviso("Versão atualizada com sucesso.");
      setAvisoTipo("success");
      setTimeout(() => {
        onHide();
      }, 2000);
    },
  });

  const handleSubmit = (event: any) => {
    event.preventDefault();
    if (selectedVersionId) {
      mutation.mutate(selectedVersionId);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Selecionar Versão para Atualização</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-4">
            <span
              className="spinner-border spinner-border-sm"
              aria-hidden="true"
            ></span>
            <span className="ms-2">Carregando versões disponíveis...</span>
          </div>
        ) : error ? (
          <Alert variant="danger">
            Erro ao carregar as versões. Por favor, tente novamente.
          </Alert>
        ) : (
          <form className="w-100" onSubmit={handleSubmit}>
            {aviso && (
              <Alert variant={avisoTipo} className="mb-3">
                {aviso}
              </Alert>
            )}

            <div className="mb-3">
              <h5>Versões Disponíveis</h5>
              <div className="table-responsive" style={{ maxHeight: "400px" }}>
                <Table hover>
                  <thead className="sticky-top bg-body">
                    <tr>
                      <th></th>
                      <th>Tag</th>
                      <th>Descrição</th>
                      <th>Data</th>
                      <th>Instalador</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.data?.map((versao: VersaoDto) => (
                      <tr
                        key={versao.id}
                        className={
                          selectedVersionId === versao.id ? "table-primary" : ""
                        }
                        style={{ cursor: "pointer" }}
                        onClick={() => setSelectedVersionId(versao.id)}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <Form.Check
                            type="radio"
                            name="versionId"
                            checked={selectedVersionId === versao.id}
                            onChange={() => setSelectedVersionId(versao.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td>{versao.tag}</td>
                        <td>
                          {versao.descricao?.length > 80
                            ? `${versao.descricao.substring(0, 80)}...`
                            : versao.descricao}
                        </td>
                        <td>
                          {format(new Date(versao.createdAt), 'dd/MM/yyyy HH:mm')}
                        </td>
                        <td>
                          {versao.instalador && (
                            <Badge pill bg="success">
                              Instalador
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>

            <div className="d-flex justify-content-end mt-3">
              <Button
                variant="secondary"
                onClick={onHide}
                className="me-2"
                disabled={mutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={mutation.isPending || !selectedVersionId}
              >
                {mutation.isPending ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      aria-hidden="true"
                    ></span>
                    Atualizando...
                  </>
                ) : (
                  "Confirmar Atualização"
                )}
              </Button>
            </div>
          </form>
        )}
      </Modal.Body>
    </Modal>
  );
};

// Helper function to format file sizes
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Client Details Modal Component
const ClienteDetalhesModal = ({
  cliente,
  show,
  onHide,
  onRenewToken,
  onBackup,
  onAmbiente,
  onVendaNaoFiscal,
  isBackupLoading,
  switchChangingIds,
  onVendaNaoFiscalToggle,
  onProducaoToggle,
  whitelabelChangingIds,
  onWhitelabelToggle,
  initialTab = "VisaoGeral",
}: {
  cliente: ClienteType | null;
  show: boolean;
  onHide: () => void;
  onRenewToken: (id: string) => void;
  onBackup: (id: string) => void;
  onAmbiente: (id: string) => void;
  onVendaNaoFiscal: (id: string) => void;
  isBackupLoading: boolean;
  switchChangingIds: string[];
  onVendaNaoFiscalToggle: (id: string, currentState: boolean) => void;
  onProducaoToggle: (id: string, currentState: boolean) => void;
  whitelabelChangingIds: string[];
  onWhitelabelToggle: (id: string, currentState: boolean) => void;
  initialTab?: string;
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);

  // Update activeTab whenever initialTab changes
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  if (!cliente) return null;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <div className="d-flex flex-column">
            <small className="text-muted">{formatCNPJ(cliente.cnpj)}</small>
            <h4>{cliente.razaoSocial?.toUpperCase()}</h4>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || "VisaoGeral")}
          id="cliente-detalhes-tabs"
          className="mb-3"
          justify
        >
          <Tab eventKey="VisaoGeral" title="Visão Geral">
            <div className="w-100 d-flex justify-content-between">
              <div className="d-flex flex-column">
                <Card className="mt-2" title="Hardware ID">
                  <Card.Body>
                    <FontAwesomeIcon
                      icon={faComputer}
                      style={{ marginRight: "5px" }}
                    />
                    {cliente.hardwareId}
                  </Card.Body>
                </Card>
                <Card className="mt-2" title="Data Criação">
                  <Card.Body>
                    <FontAwesomeIcon
                      icon={faBusinessTime}
                      style={{ marginRight: "5px" }}
                    />
                    {format(new Date(cliente.backend?.licenca?.issued), 'dd/MM/yyyy HH:mm:ss')}
                  </Card.Body>
                </Card>
                <Card className="mt-2" title="Data Expiração">
                  <Card.Body>
                    <FontAwesomeIcon
                      icon={faCalendarTimes}
                      style={{ marginRight: "5px" }}
                    />

                    {format(new Date(cliente.backend?.licenca?.expiration), 'dd/MM/yyyy HH:mm:ss')}
                  </Card.Body>
                </Card>
                <Card className="mt-2" title="Roles">
                  <Card.Body>
                    {cliente.backend?.licenca?.roles &&
                      cliente.backend?.licenca?.roles.map((role, index) => (
                        <div key={index}>
                          <FontAwesomeIcon
                            icon={faKey}
                            style={{ marginRight: "5px" }}
                          />
                          {role}
                        </div>
                      ))}
                  </Card.Body>
                </Card>
              </div>
              <Card
                className="mt-2"
                title="Chave pública"
                style={{ maxWidth: "600px", maxHeight: "250px" }}
              >
                <Card.Body style={{ wordWrap: "break-word" }}>
                  <FontAwesomeIcon
                    icon={faCode}
                    style={{ marginRight: "5px" }}
                  />
                  {cliente.backend?.licenca?.pub}
                </Card.Body>
              </Card>
            </div>
            <div className="d-flex mt-4 align-items-end justify-content-end">
              <Button
                className="me-2"
                variant="outline-primary"
                onClick={() => onRenewToken(cliente.connectionId)}
              >
                Renovar Licença
              </Button>
              <Button
                className="me-2"
                variant="outline-primary"
                onClick={() => onAmbiente(cliente.connectionId)}
              >
                {cliente.homologacao ? "Produção" : "Homologação"}
              </Button>
              <Button
                className="me-2"
                variant="outline-primary"
                loading={isBackupLoading}
                loadingPosition="start"
                onClick={() => onBackup(cliente.connectionId)}
              >
                Backup
              </Button>
              <Form className="ms-3 d-flex align-items-center">
                <Form.Check
                  type="switch"
                  id={`venda-nao-fiscal-switch-modal-${cliente.connectionId}`}
                  label="Venda Não Fiscal"
                  checked={
                    cliente.backend?.configuracao?.vendaNaoFiscal || false
                  }
                  onChange={() =>
                    onVendaNaoFiscalToggle(
                      cliente.connectionId,
                      cliente.backend?.configuracao?.vendaNaoFiscal || false
                    )
                  }
                  disabled={switchChangingIds.includes(cliente.connectionId)}
                />
                {switchChangingIds.includes(cliente.connectionId) && (
                  <div className="ms-2">
                    <span
                      className="spinner-border spinner-border-sm text-secondary"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  </div>
                )}
              </Form>
              <Form className="ms-3 d-flex align-items-center">
                <Form.Check
                  type="switch"
                  id={`whitelabel-switch-modal-${cliente.connectionId}`}
                  label="Whitelabel"
                  checked={cliente.whitelabelHabilitado || false}
                  onChange={() =>
                    onWhitelabelToggle(
                      cliente.connectionId,
                      cliente.whitelabelHabilitado || false
                    )
                  }
                  disabled={whitelabelChangingIds.includes(
                    cliente.connectionId
                  )}
                />
                {whitelabelChangingIds.includes(cliente.connectionId) && (
                  <div className="ms-2">
                    <span
                      className="spinner-border spinner-border-sm text-secondary"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  </div>
                )}
              </Form>
            </div>
          </Tab>

          <Tab eventKey="Frontend" title="Frontend">
            <div className="w-100 d-flex flex-wrap justify-content-between">
              {cliente.frontends?.map((frontend, frontendIndex) => (
                <div key={frontendIndex}>
                  <Badge pill bg="secondary" title="Versão do terminal">
                    <Monitor style={{ marginRight: "5px" }} />
                    {frontend.versao}
                  </Badge>
                  <Card className="mt-2" title="Usuário">
                    <Card.Body>
                      <FontAwesomeIcon
                        icon={faCircleUser}
                        style={{ marginRight: "5px" }}
                      />
                      {frontend.usuario || "N/A"}
                    </Card.Body>
                  </Card>
                  <Card className="mt-2" title="Máquina ID">
                    <Card.Body>
                      <Monitor
                        style={{ marginRight: "5px" }}
                      />
                      {frontend.maquinaId}
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          </Tab>

          <Tab eventKey="HealthCheck" title="HealthCheck" unmountOnExit>
            <HealthCheck connectionId={cliente.connectionId} />
          </Tab>

          <Tab eventKey="Atualização" title="Atualização" unmountOnExit>
            <Atualizacao connectionId={cliente.connectionId} />
          </Tab>

          <Tab eventKey="Terminal" title="Terminal" unmountOnExit>
            <div style={{ height: "400px" }}>
              <Term connectionId={cliente.connectionId} />
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

function Servidores() {
  const { data, error, isLoading, refetch } = useQuery<ClienteType[]>({
    queryFn: fetchClientes,
    queryKey: ["clientes"],
    refetchInterval: 1000,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedCNPJs, setCopiedCNPJs] = useState<Record<string, boolean>>({});
  const [selectedCliente, setSelectedCliente] = useState<ClienteType | null>(
    null
  );
  const [showModal, setShowModal] = useState(false);
  const [activeModalTab, setActiveModalTab] = useState("VisaoGeral");
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [showVersionByIdModal, setShowVersionByIdModal] = useState(false);

  const backup = useMutation<string, string, string>({
    mutationFn: (data) => {
      return axios.post(`/api/hubs/Cliente/backup/${data}`);
    },
  });

  const [switchChangingIds, setSwitchChangingIds] = useState<string[]>([]);
  const [prodChangingIds, setProdChangingIds] = useState<string[]>([]);
  const [whitelabelChangingIds, setWhitelabelChangingIds] = useState<
    string[]
  >([]);

  const handleRenewToken = async (connectionId: any) => {
    try {
      const response = await fetch(`/api/hubs/backend/${connectionId}/renew`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization:
            axios.defaults?.headers?.common?.["Authorization"]?.toString() ||
            "",
        },
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      console.log("Licença renovada com sucesso.");
    } catch (err) {
      console.error("Erro ao renovar licença do cliente: ", err);
    }
  };

  const handleAmbiente = async (connectionId: any) => {
    const cliente = data?.find(
      (cliente: any) => cliente.connectionId === connectionId
    );
    console.log("Configuracao", cliente?.backend.configuracao);
    try {
      const response = await fetch(
        `/api/hubs/backend/${connectionId}/configurar`,
        {
          method: "POST",
          body: JSON.stringify({
            producao: !cliente?.backend.configuracao.producao,
            vendaNaoFiscal: cliente?.backend.configuracao.vendaNaoFiscal,
          }),
          headers: {
            "Content-Type": "application/json",
            Authorization:
              axios.defaults?.headers?.common?.["Authorization"]?.toString() ||
              "",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      console.log("Ambiente alternado com sucesso.");
    } catch (err) {
      console.error("Erro ao alternar ambeinte do cliente: ", err);
    }
  };

  const handleVendaNaoFiscal = async (connectionId: any) => {
    const cliente = data?.find(
      (cliente: any) => cliente.connectionId === connectionId
    );
    console.log("Configuracao", cliente?.backend.configuracao);
    try {
      const response = await fetch(
        `/api/hubs/backend/${connectionId}/configurar`,
        {
          method: "POST",
          body: JSON.stringify({
            producao: cliente?.backend.configuracao.producao,
            vendaNaoFiscal: !cliente?.backend.configuracao.vendaNaoFiscal,
          }),
          headers: {
            "Content-Type": "application/json",
            Authorization:
              axios.defaults?.headers?.common?.["Authorization"]?.toString() ||
              "",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      console.log("Ambiente alternado com sucesso.");
    } catch (err) {
      console.error("Erro ao alternar ambeinte do cliente: ", err);
    }
  };

  const handleVendaNaoFiscalToggle = async (
    connectionId: string,
    currentState: boolean
  ) => {
    // Add to loading state
    setSwitchChangingIds((prev) => [...prev, connectionId]);

    try {
      await handleVendaNaoFiscal(connectionId);
      // Success handling (optional toast/notification can be added here)
    } catch (error) {
      console.error("Error toggling venda não fiscal:", error);
      // Error handling (optional toast/notification can be added here)
    } finally {
      // Remove from loading state
      setSwitchChangingIds((prev) => prev.filter((id) => id !== connectionId));
    }
  };

  const handleProducaoToggle = async (
    connectionId: string,
    currentState: boolean
  ) => {
    // Add to loading state
    setProdChangingIds((prev) => [...prev, connectionId]);

    try {
      await handleAmbiente(connectionId);
      // Success handling if needed
    } catch (error) {
      console.error("Error toggling ambiente:", error);
      // Error handling if needed
    } finally {
      // Remove from loading state
      setProdChangingIds((prev) => prev.filter((id) => id !== connectionId));
    }
  };

  const handleWhitelabelToggle = async (
    connectionId: string,
    currentState: boolean
  ) => {
    setWhitelabelChangingIds((prev) => [...prev, connectionId]);

    try {
      const response = await fetch(
        `/api/hubs/backend/${connectionId}/whitelabel`,
        {
          method: "POST",
          body: JSON.stringify({ habilitado: !currentState }),
          headers: {
            "Content-Type": "application/json",
            Authorization:
              axios.defaults?.headers?.common?.["Authorization"]?.toString() ||
              "",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }
    } catch (error) {
      console.error("Error toggling whitelabel:", error);
    } finally {
      setWhitelabelChangingIds((prev) =>
        prev.filter((id) => id !== connectionId)
      );
    }
  };

  const handleCopyCNPJ = (cnpj: string) => {
    navigator.clipboard
      .writeText(cnpj)
      .then(() => {
        console.log("CNPJ copied to clipboard");

        // Set this CNPJ as copied
        setCopiedCNPJs((prev) => ({
          ...prev,
          [cnpj]: true,
        }));

        // Reset after 5 seconds
        setTimeout(() => {
          setCopiedCNPJs((prev) => ({
            ...prev,
            [cnpj]: false,
          }));
        }, 5000);
      })
      .catch((err) => {
        console.error("Could not copy CNPJ: ", err);
      });
  };

  const handleOpenDetalhes = (cliente: ClienteType) => {
    setSelectedCliente(cliente);
    setActiveModalTab("VisaoGeral");
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleOpenAtualizacao = (cliente: ClienteType) => {
    setSelectedCliente(cliente);
    setActiveModalTab("Atualização");
    setShowModal(true);
  };

  const handleOpenVersionModal = (cliente: ClienteType) => {
    setSelectedCliente(cliente);
    setShowVersionModal(true);
  };

  const handleCloseVersionModal = () => {
    setShowVersionModal(false);
  };

  const handleOpenVersionByIdModal = (cliente: ClienteType) => {
    setSelectedCliente(cliente);
    setShowVersionByIdModal(true);
  };

  const handleCloseVersionByIdModal = () => {
    setShowVersionByIdModal(false);
  };

  return (
    <>
      <div>
        <PageBreadcrumb items={servidoresBreadcrumbItems} />
        <div className="mb-9">
          <h2 className="mb-5">Servidores</h2>
          <div className="mt-3 d-flex w-100 justify-content-center">
            <SearchBox
              className="w-100"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar"
            />
          </div>

          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3 mt-3">
            {data &&
              data
                .filter(
                  (cliente) =>
                    cliente.razaoSocial
                      ?.toLowerCase()
                      .includes(searchTerm.toLowerCase()) ||
                    cliente.cnpj
                      ?.toLowerCase()
                      .includes(searchTerm.toLowerCase()) ||
                    cliente.hardwareId
                      ?.toLowerCase()
                      .includes(searchTerm.toLowerCase())
                )
                .map((item, index) => (
                  <div className="col" key={item.connectionId}>
                    <Card
                      className="h-100"
                      style={{
                        borderWidth: "2px",
                        borderRadius: "8px",
                        boxShadow: "0 0 5px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      <Card.Header>
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="d-flex flex-column">
                            <div className="d-flex align-items-center mb-1">
                              <small className="text-muted me-2">
                                {formatCNPJ(item.cnpj)}
                              </small>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "20px",
                                  height: "20px",
                                  borderRadius: "4px",
                                  boxShadow: "0 0 5px rgba(0, 0, 0, 0.1)",
                                }}
                              >
                                <Button
                                  variant="link"
                                  className="p-0"
                                  style={{ color: "#6b6b6b" }}
                                  onClick={() => handleCopyCNPJ(item.cnpj)}
                                  title={
                                    copiedCNPJs[item.cnpj]
                                      ? "CNPJ copiado!"
                                      : "Copiar CNPJ"
                                  }
                                >
                                  <FontAwesomeIcon
                                    icon={
                                      copiedCNPJs[item.cnpj] ? faCheck : faCopy
                                    }
                                    size="xs"
                                  />
                                </Button>
                              </div>
                            </div>
                            <div className="d-flex align-items-center">
                              <Home
                                size={19}
                                style={{ marginRight: "10px" }}
                              />
                              <Link
                                className="m-0 text-truncate mt-auto text-body-emphasis"
                                title={item.razaoSocial?.toUpperCase()}
                                to={`/revenda/clientes/${item?.backend?.empresa?.id}`}
                              >
                                {item.razaoSocial?.toUpperCase().length > 28
                                  ? item.razaoSocial
                                    ?.toUpperCase()
                                    .substring(0, 28) + "..."
                                  : item.razaoSocial?.toUpperCase()}
                              </Link>
                            </div>
                          </div>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          <Badge
                            pill
                            bg="secondary"
                            title="Quantidade terminais"
                          >
                            <Monitor
                              style={{ marginRight: "5px" }}
                            />
                            {item.quantidadeTerminais}
                          </Badge>

                          {item.versaoBackend ? (
                            <Badge
                              pill
                              bg="secondary"
                              title="Versão do backend"
                            >
                              <Server
                                style={{ marginRight: "5px" }}
                              />
                              {item.versaoBackend}
                            </Badge>
                          ) : (
                            <Badge pill bg="danger" title="Versão do backend">
                              <Server
                                style={{ marginRight: "5px" }}
                              />
                              {"Indisponível"}
                            </Badge>
                          )}

                          {item.homologacao === false ? (
                            <Badge
                              pill
                              bg="success"
                              title="Ambiente de produção"
                            >
                              <Settings />
                            </Badge>
                          ) : (
                            <Badge
                              pill
                              bg="danger"
                              title="Ambiente de homologação"
                            >
                              <Settings />
                            </Badge>
                          )}

                          {/* Sync status indicator */}
                          {item.backend?.statusSincronizacao && (
                            <Badge
                              pill
                              bg={
                                item.backend.statusSincronizacao.status === 0
                                  ? "info"
                                  : "warning"
                              }
                              title={
                                item.backend.statusSincronizacao.status === 0
                                  ? `Última sincronização: ${item.backend.statusSincronizacao.since}`
                                  : `Erro de sincronização desde: ${item.backend.statusSincronizacao.since}
Mensagem: ${item.backend.statusSincronizacao.mensagem}`
                              }
                            >
                              <UploadCloud />
                            </Badge>
                          )}
                        </div>

                        <div className="mb-2">
                          <small className="text-muted d-flex align-items-center">
                            <FontAwesomeIcon
                              icon={faComputer}
                              style={{ marginRight: "8px" }}
                            />
                            <span
                              className="text-truncate"
                              title={item.hardwareId}
                            >
                              {item.hardwareId}
                            </span>
                          </small>
                        </div>

                        <div className="mb-2">
                          <small className="text-muted d-flex align-items-center">
                            <FontAwesomeIcon
                              icon={faCalendarTimes}
                              style={{ marginRight: "8px" }}
                            />
                            Expira em:{" "}
                            {format(new Date(item.backend?.licenca?.expiration), 'dd/MM/yyyy HH:mm:ss')}
                          </small>
                        </div>

                        <div className="d-flex flex-wrap gap-2 mt-3">
                          <Button
                            size="sm"
                            className="d-flex gap-1"
                            variant="outline-primary"
                            onClick={() => handleOpenDetalhes(item)}
                          >
                            <Info /> Ações
                          </Button>

                          <Button
                            size="sm"
                            className="d-flex gap-1"
                            variant="outline-primary"
                            onClick={() => handleOpenVersionByIdModal(item)}
                          >
                            <FontAwesomeIcon
                              icon={faSync}
                              className="me-1"
                              size="sm"
                            />{" "}
                            Atualizar
                          </Button>

                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="d-flex gap-1"
                            loading={
                              backup.isPending &&
                              backup.variables === item.connectionId
                            }
                            loadingPosition="start"
                            onClick={() => backup.mutate(item.connectionId)}
                          >
                            <Save size={13} /> Backup
                          </Button>
                        </div>

                        {/* Add both switches in a row with labels */}
                        <div className="d-flex flex-wrap mt-3 gap-4">
                          {/* Production/Homologation switch */}
                          <div className="d-flex align-items-center">
                            <Form>
                              <Form.Check
                                type="switch"
                                id={`ambiente-switch-${item.connectionId}`}
                                label="Produção"
                                checked={!item.homologacao}
                                onChange={() =>
                                  handleProducaoToggle(
                                    item.connectionId,
                                    !item.homologacao
                                  )
                                }
                                disabled={prodChangingIds.includes(
                                  item.connectionId
                                )}
                              />
                            </Form>
                            {prodChangingIds.includes(item.connectionId) && (
                              <div className="ms-2">
                                <span
                                  className="spinner-border spinner-border-sm text-secondary"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                              </div>
                            )}
                          </div>

                          {/* Existing Venda Não Fiscal switch */}
                          <div className="d-flex align-items-center">
                            <Form>
                              <Form.Check
                                type="switch"
                                id={`venda-nao-fiscal-switch-${item.connectionId}`}
                                label="Venda Não Fiscal"
                                checked={
                                  item.backend?.configuracao?.vendaNaoFiscal ||
                                  false
                                }
                                onChange={() =>
                                  handleVendaNaoFiscalToggle(
                                    item.connectionId,
                                    item.backend?.configuracao
                                      ?.vendaNaoFiscal || false
                                  )
                                }
                                disabled={switchChangingIds.includes(
                                  item.connectionId
                                )}
                              />
                            </Form>
                            {switchChangingIds.includes(item.connectionId) && (
                              <div className="ms-2">
                                <span
                                  className="spinner-border spinner-border-sm text-secondary"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                              </div>
                            )}
                          </div>

                          {/* Whitelabel access switch */}
                          <div className="d-flex align-items-center">
                            <Form>
                              <Form.Check
                                type="switch"
                                id={`whitelabel-switch-${item.connectionId}`}
                                label="Whitelabel"
                                checked={item.whitelabelHabilitado || false}
                                onChange={() =>
                                  handleWhitelabelToggle(
                                    item.connectionId,
                                    item.whitelabelHabilitado || false
                                  )
                                }
                                disabled={whitelabelChangingIds.includes(
                                  item.connectionId
                                )}
                              />
                            </Form>
                            {whitelabelChangingIds.includes(
                              item.connectionId
                            ) && (
                              <div className="ms-2">
                                <span
                                  className="spinner-border spinner-border-sm text-secondary"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                              </div>
                            )}
                          </div>
                        </div>
                      </Card.Body>
                      <Card.Footer>
                        <small className="text-muted">
                          Registrado em:{" "}
                          {new Date(item.registradoEm).toLocaleDateString()}{" "}
                          {new Date(item.registradoEm).toLocaleTimeString()}
                        </small>
                      </Card.Footer>
                    </Card>
                  </div>
                ))}
          </div>

          {/* Create a simple detail page route to show all information when "Detalhes" is clicked */}
        </div>
      </div>

      <ClienteDetalhesModal
        cliente={selectedCliente}
        show={showModal}
        onHide={handleCloseModal}
        onRenewToken={handleRenewToken}
        onBackup={backup.mutate}
        onAmbiente={handleAmbiente}
        onVendaNaoFiscal={handleVendaNaoFiscal}
        isBackupLoading={
          backup.isPending && backup.variables === selectedCliente?.connectionId
        }
        switchChangingIds={switchChangingIds}
        onVendaNaoFiscalToggle={handleVendaNaoFiscalToggle}
        onProducaoToggle={handleProducaoToggle}
        whitelabelChangingIds={whitelabelChangingIds}
        onWhitelabelToggle={handleWhitelabelToggle}
        initialTab={activeModalTab}
      />

      <VersionSelectionModal
        show={showVersionModal}
        onHide={handleCloseVersionModal}
        connectionId={selectedCliente?.connectionId || ""}
      />

      <VersionSelectionByIdModal
        show={showVersionByIdModal}
        onHide={handleCloseVersionByIdModal}
        connectionId={selectedCliente?.connectionId || ""}
      />
    </>
  );
}

export default Servidores;
